import { useState, useRef, useEffect } from 'react'
import { MessageCircle, CheckCircle2, MoreHorizontal, Archive, Link2, FolderPlus } from 'lucide-react'
import { type FeedbackItem } from '../../lib/feedbackApi'
import { formatRelativeTime } from '../../lib/format'
import { FeedbackVoteButton } from './FeedbackVoteButton'

const TYPE_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  bug: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', label: 'Bug' },
  feature: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', label: 'Feature' },
  improvement: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', label: 'Improvement' },
  integration: { bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', label: 'Integration' },
}

const STATUS_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  backlog: { bg: 'bg-secondary', text: 'text-muted-foreground', label: 'Backlog' },
  in_progress: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', label: 'In Progress' },
  done: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', label: 'Done' },
}

interface FeedbackCardProps {
  item: FeedbackItem
  onSelect: (id: number) => void
  onVoteChange?: (id: number, hasVoted: boolean, voteCount: number) => void
  onArchive?: (id: number) => void
  onAddToTheme?: (id: number) => void
  isAdmin?: boolean
}

export function FeedbackCard({ item, onSelect, onVoteChange, onArchive, onAddToTheme, isAdmin }: FeedbackCardProps) {
  const typeBadge = item.type ? TYPE_BADGE[item.type] : null
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Use previous_status for styling when dismissed/archived
  const isInArchive = item.status === 'dismissed' || item.status === 'archived'
  const displayStatus = isInArchive ? (item.previous_status || 'backlog') : item.status
  const isDone = displayStatus === 'done'
  const isInProgress = displayStatus === 'in_progress'

  const authorDisplay = item.is_anonymous && !item.author?.name
    ? 'Anonymous'
    : item.author?.name || item.author_name || 'Unknown'

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  return (
    <div
      onClick={() => onSelect(item.id)}
      className={`group border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer relative ${
        isDone
          ? 'bg-card/50 border-border/50 opacity-60'
          : isInProgress
            ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800/40 hover:border-amber-300 dark:hover:border-amber-700/50'
            : 'bg-card border-border hover:border-primary/30'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Vote button */}
        <FeedbackVoteButton
          feedbackId={item.id}
          initialHasVoted={item.has_voted}
          initialVoteCount={item.vote_count}
          onVoteChange={(hasVoted, voteCount) =>
            onVoteChange?.(item.id, hasVoted, voteCount)
          }
        />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {isDone && (
              <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
            )}
            <h3 className={`text-sm font-semibold truncate ${
              isDone ? 'line-through text-muted-foreground' : 'text-foreground'
            }`}>
              {item.title}
            </h3>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {item.status === 'dismissed' && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/10 text-red-600 dark:text-red-400">
                Dismissed
              </span>
            )}
            {item.status === 'archived' && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-secondary text-muted-foreground">
                Archived
              </span>
            )}
            {typeBadge && (
              <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${typeBadge.bg} ${typeBadge.text}`}>
                {typeBadge.label}
              </span>
            )}
            {(() => {
              const badge = STATUS_BADGE[displayStatus] || STATUS_BADGE.backlog
              return (
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}>
                  {badge.label}
                </span>
              )
            })()}
            {isAdmin && item.themes && item.themes.length > 0 && item.themes.map((t) => (
              <span key={t.id} className="text-[11px] text-primary truncate">
                #{t.name}
              </span>
            ))}
          </div>

          {/* Meta */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>{authorDisplay}</span>
            <span>{formatRelativeTime(item.created_at)}</span>
            {item.comment_count > 0 && (
              <span className="flex items-center gap-1">
                <MessageCircle className="w-3 h-3" />
                {item.comment_count}
              </span>
            )}
          </div>
        </div>

        {/* 3-dot context menu */}
        <div ref={menuRef} className="relative shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setMenuOpen((v) => !v)
            }}
            className="p-1 rounded-lg hover:bg-secondary transition-colors opacity-0 group-hover:opacity-100"
          >
            <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 z-20 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px]">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  navigator.clipboard.writeText(`${window.location.origin}/feedback?id=${item.id}`)
                }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-secondary transition-colors"
              >
                <Link2 className="w-3.5 h-3.5" />
                Copy link
              </button>
              {isAdmin && onAddToTheme && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setMenuOpen(false)
                    onAddToTheme(item.id)
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-secondary transition-colors"
                >
                  <FolderPlus className="w-3.5 h-3.5" />
                  Add to theme
                </button>
              )}
              {isAdmin && onArchive && !isInArchive && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setMenuOpen(false)
                    onArchive(item.id)
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-secondary transition-colors"
                >
                  <Archive className="w-3.5 h-3.5" />
                  Archive
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
